/* global API, Site, I18n */

import { createApp } from 'vue';
import Paginate from 'vuejs-paginate-next';
import fullcalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import rrulePlugin from '@fullcalendar/rrule';
import fiLocale from '@fullcalendar/core/locales/fi';
import svLocale from '@fullcalendar/core/locales/sv';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useOpenEmbed, useCloseEmbed, useEmbedData, useCreateEmbed, useCopyEmbed } from './composables/embed';
import ld from 'lodash';
let _ = ld.noConflict();

import {
	useQuery,
	useGetTitle,
	useGetShortDesc,
	useGetDesc,
	useHasShortDesc,
	useGetTermCounts,
	useGetCount,
	useCreateParams,
	useGetParams,
	useGetReferrer,
	useFormatDate,
	useFormatEventDate,
	useFormatEventDateWithTime,
} from './composables/data';

import { useModalData, useCloseModal } from './composables/modal';

import { usePaginationData, usePages, useChangePage, usePageStart, usePageEnd } from './composables/paging';

// Vue element
const vueSelector = '.vue-events';
const vueElement = document.querySelector(vueSelector);

if (vueElement) {
	let fcOptions = localStorage.getItem('fcOptions');

	let initialView;
	let initialDate;

	if (fcOptions) {
		fcOptions = JSON.parse(fcOptions);

		const { expires } = fcOptions;
		const date = new Date();

		if (expires && expires > date.getTime()) {
			initialView = fcOptions.view;
			initialDate = fcOptions.date;
		} else {
			localStorage.removeItem('fcOptions');
		}
	}

	createApp({
		components: {
			Paginate,
			fullcalendar,
			Datepicker,
		},
		data() {
			return {
				lang: Site.lang_slug,
				results: {},
				dataSet: [],
				sources: {
					tapahtumat: [],
					teemapaivat: [],
				},
				calendarOptions: {
					plugins: [dayGridPlugin, timeGridPlugin, listPlugin, rrulePlugin],
					locale: Site.lang_slug === 'sv' ? svLocale : Site.lang_slug === 'fi' ? fiLocale : null,
					initialView: initialView || 'dayGridMonth',
					initialDate: initialDate || new Date(),
					headerToolbar: {
						left: 'dayGridMonth,timeGridWeek,timeGridDay',
						center: 'title',
						right: 'prevYear,prev,next,nextYear',
					},
					navLinks: true,
					displayEventTime: false,
					eventInteractive: true,
					eventSources: [],
					eventClick: this.eventClick,
					eventDidMount: this.eventDidMount,
					datesSet: this.datesSet,
					buttonHints: {
						prev: I18n.previous,
						next: I18n.next,
					},
				},
				dpOpts: {
					locale: 'fi',
					format: 'dd.MM.yyyy',
					placeholder: '01.01.2022',
					selectText: I18n.select,
					cancelText: I18n.cancel,
				},
				helpers: {
					loading: true,
				},
				useQuery,
				usePaginationData,
				useModalData,
				emptyQuery: _.cloneDeep(useQuery),
				events: [],
				eventsList: [],
				format: 'calendar',
				termCounts: {},
				useEmbedData,
				showPostType: false,
			};
		},
		computed: {
			/**
			 * Is the app currently loading?
			 * @returns {boolean} True if loading, false if not
			 */
			loading() {
				if (this.helpers.loading) {
					return true;
				}

				return false;
			},
		},
		async mounted() {
			this.lang = Site.lang_slug;

			// Set the search params from the URL
			this.useGetParams();

			// Setup the pagination data
			this.usePaginationData.postsPerPage = 10;

			this.getTeemapaivat();
		},
		methods: {
			/**
			 * Hakufunktio
			 * @param {boolean} log Logataanko haku
			 */
			search(log = false) {
				this.helpers.loading = true;

				this.useQuery.log = log;
				this.useQuery.post_type = 'tapahtumat';

				this.useQuery.order = 'ASC';
				this.useQuery.orderby = 'meta_value';
				this.useQuery.meta_key = 'start_date';

				this.useQuery.posts_per_page = -1;
				this.useQuery.taxonomies = true;

				// Reset the pagination page to 1
				this.usePaginationData.page = 1;

				const deepCopy = _.cloneDeep(this.useQuery);

				this.useCreateParams('tapahtumat', deepCopy);

				fetch(`${API.url}/search`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json; charset=utf-8',
						'X-WP-Nonce': API.nonce,
					},
					body: JSON.stringify(deepCopy),
				})
					.then((res) => res.json())
					.then((response) => {
						this.results.tapahtumat = response;

						this.eventsToArray(response.results);

						this.termCounts = {};
						this.getTermCounts('tapahtuman_kategoriat');
						this.getTermCounts('tapahtuman_kohderyhmat');
						this.getTermCounts('alueet');

						this.setDataSet();

						this.helpers.loading = false;
					})
					.catch((error) => console.error(error));
			},
			empty() {
				this.useQuery = _.cloneDeep(this.emptyQuery);

				this.search();
			},
			getTermCounts(tax = 'tapahtuman_kategoriat') {
				const results = this.results?.tapahtumat?.results;
				const taxonomies = this.results?.tapahtumat?.taxonomies;

				if (taxonomies?.[tax]) {
					const terms = taxonomies[tax];

					terms.forEach((term) => {
						this.termCounts[term.term_id] = {
							term_id: term.term_id,
							count: 0,
						};
					});

					results?.forEach((result) => {
						if (!result.taxonomies[tax]) {
							return;
						}

						const resultTerms = result.taxonomies[tax];

						resultTerms.forEach((resultTerm) => {
							this.termCounts[resultTerm.term_id].count += 1;

							if (resultTerm.parent !== 0) {
								// Also increment the parent term count
								this.termCounts[resultTerm.parent].count += 1;
							}
						});
					});
				}
			},
			combineEvents() {
				this.calendarOptions.eventSources = [];

				this.calendarOptions.eventSources.push(this.sources.tapahtumat);
				this.calendarOptions.eventSources.push(this.sources.teemapaivat);
			},
			/**
			 * Muuttaa haun tuloksen arrayksi, jota FullCalendar ymmärtää
			 * @param {object} results Haun tulokset
			 */
			eventsToArray(results) {
				const eventSource = {
					events: [],
					color: '#05acc1', // ympyrän väri
					className: 'mappa-event',
				};

				if (results) {
					const resultsArr = new Map(Object.entries(results));

					resultsArr.forEach((result) => {
						if (!result.no_date) {
							const start = new Date(result.start_date);
							const end = new Date(result.end_date);

							const diff = end.getTime() - start.getTime();
							const diffInDays = diff / (1000 * 60 * 60 * 24);

							const categories = result.taxonomies?.tapahtuman_kategoriat || [];
							let bgColor = '#05acc1';
							let textColor = '#ffffff';

							if (categories.length > 0) {
								const category = categories[0].slug;

								if (category === 'koulutukset') {
									bgColor = '#027B8A';
									textColor = '#ffffff';
								} else if (category === 'tapahtumat') {
									bgColor = '#76B729';
									textColor = '#ffffff';
								} else if (category === 'teemapaivat-ja-tai-kampanjat') {
									bgColor = '#C20068';
									textColor = '#ffffff';
								} else if (category === 'toimintaa-esim-retket-ja-leirit') {
									bgColor = '#0C7333';
									textColor = '#ffffff';
								}
							}

							const formatToISO = (date) => {
								const year = date.getFullYear();
								const month = String(date.getMonth() + 1).padStart(2, '0');
								const day = String(date.getDate()).padStart(2, '0');

								return `${year}-${month}-${day}`;
							};

							// Remove the time from the dates
							const startDate = new Date(result.start_date);
							const endDate = new Date(result.end_date);

							// Format the date to ISO string without time
							// const formattedStartDate = formatToISO(startDate);
							// let formattedEndDate = formatToISO(endDate);

							const formattedStartDate = startDate;
							let formattedEndDate = endDate;

							if (diffInDays >= 7) {
								// formattedEndDate = formattedStartDate; // If the event lasts a week or more, use the start date as the end date
								formattedEndDate = startDate;
							}

							const event = {
								title: this.useGetTitle(result),
								start: formattedStartDate,
								end: formattedEndDate,
								description: result[this.lang]?.description ? result[this.lang]?.description : result.fi?.description,
								id: result.post_id,
								eventId: result.post_id,
								color: bgColor,
								textColor,
								extendedProps: {
									href: result.permalink,
									description: this.useGetDesc(result),
									short_desc: this.useGetShortDesc(result),
									owner: result.owner,
									start: result.start_date,
									end: result.end_date,
								},
							};

							eventSource.events.push(event);
						} else {
							this.eventsList.push(result);
						}
					});

					this.events = eventSource;
				}

				this.sources.tapahtumat = eventSource;

				this.combineEvents();
			},
			getTeemapaivat() {
				fetch(`${API.url}/calendar`, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json; charset=utf-8',
						'X-WP-Nonce': API.nonce,
					},
				})
					.then((res) => res.json())
					.then((response) => {
						this.teemapaivatToArray(response);
					})
					.catch((err) => {
						console.error(err);
					});
			},
			teemapaivatToArray(results) {
				const eventSource = {
					events: [],
					color: '#f0f0f0', // ympyrän väri
					className: 'teemapaiva-event',
				};

				results.forEach((result) => {
					const event = {
						title: result.title[this.lang] || result.title.fi,
						description: result.description[this.lang] || result.description.fi,
						rrule: result.rrule,
						extendedProps: {
							description: result.description[this.lang] || result.description.fi,
						},
					};

					eventSource.events.push(event);
				});

				this.sources.teemapaivat = eventSource;

				this.combineEvents();
			},
			/**
			 * Kalenterin tapahtuman klikkausevent
			 * @param {object} info FullCalendarin eventClickInfo, sisältää .el, .jsEvent, .view
			 */
			eventClick(info) {
				info.jsEvent.preventDefault();

				if (info.el?.classList?.contains('google-calendar-event')) {
					return;
				}

				const type = info.el?.classList?.contains('teemapaiva-event') ? 'teemapaiva' : 'tapahtuma';

				this.useModalData.type = type;
				this.useModalData.title = info.event.title;
				this.useModalData.description = info.event.extendedProps.short_desc
					? info.event.extendedProps.short_desc
					: info.event.extendedProps.description;
				this.useModalData.href = info.event.extendedProps.href;
				this.useModalData.owner = info.event.extendedProps.owner;
				this.useModalData.startDate = info.event.extendedProps.start;
				this.useModalData.endDate = info.event.extendedProps.end;

				this.openModal();
			},
			eventDidMount(info) {
				info.el.title = info.event.title;
			},
			openModal() {
				this.useModalData.show = true;

				document.body.classList.add('noscroll');
			},
			datesSet() {
				const fc = this.$refs.fullCalendar.getApi();

				const date = new Date();
				date.setMinutes(date.getMinutes() + 10);

				const fcSettings = {
					view: fc.view.type,
					date: fc.getDate(),
					expires: date.getTime(),
				};

				localStorage.setItem('fcOptions', JSON.stringify(fcSettings));

				this.setDataSet();
			},
			setDataSet() {
				const fc = this.$refs.fullCalendar.getApi();

				this.dataSet = [];

				const selectedDate = fc.getDate();
				const dateObject = new Date(selectedDate);

				const selectedDay = dateObject.getDate();
				const selectedWeek = new Date(
					dateObject.getFullYear(),
					dateObject.getMonth(),
					dateObject.getDate() - dateObject.getDay(),
				);
				const selectedMonth = dateObject.getMonth() + 1;
				const selectedYear = dateObject.getFullYear();

				if (fc.view.type === 'dayGridMonth') {
					this.dataSet = this.results?.tapahtumat?.results.filter((event) => {
						const month = new Date(event.start_date).getMonth() + 1;
						const year = new Date(event.start_date).getFullYear();

						if (month === selectedMonth && year === selectedYear) {
							return true;
						}

						return false;
					});
				} else if (fc.view.type === 'timeGridWeek') {
					this.dataSet = this.results?.tapahtumat?.results.filter((event) => {
						const eventDate = new Date(event.start_date);
						const weekStart = new Date(selectedWeek.getFullYear(), selectedWeek.getMonth(), selectedWeek.getDate());

						if (eventDate >= weekStart && eventDate < new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)) {
							return true;
						}

						return false;
					});
				} else if (fc.view.type === 'timeGridDay') {
					this.dataSet = this.results?.tapahtumat?.results.filter((event) => {
						const eventDate = new Date(event.start_date);
						const dayStart = new Date(selectedYear, dateObject.getMonth(), selectedDay);

						if (eventDate >= dayStart && eventDate < new Date(dayStart.getTime() + 24 * 60 * 60 * 1000)) {
							return true;
						}

						return false;
					});
				}
			},
			monthGetEvents() {
				return this.dataSet || [];
			},
			monthHasEvents() {
				if (!this.dataSet) {
					return false;
				}

				return this.dataSet.length > 0;
			},
			getViewName() {
				const fc = this.$refs.fullCalendar?.getApi();

				if (!fc) {
					return '';
				}

				if (fc.view.type === 'dayGridMonth') {
					return I18n.monthEvents;
				} else if (fc.view.type === 'timeGridWeek') {
					return I18n.weekEvents;
				} else if (fc.view.type === 'timeGridDay') {
					return I18n.dayEvents;
				}

				return '';
			},
			useFormatEventDate,
			useFormatEventDateWithTime,
			useFormatDate,
			useGetTermCounts,
			usePageStart,
			usePageEnd,
			useChangePage,
			usePages,
			useCloseModal,
			useGetTitle,
			useGetShortDesc,
			useGetDesc,
			useHasShortDesc,
			useGetCount,
			useCreateParams,
			useGetReferrer,
			useOpenEmbed,
			useCloseEmbed,
			useCreateEmbed,
			useCopyEmbed,
			useGetParams,
		},
	}).mount(vueSelector);
}
